import { API, MigrationStatus } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonVariant, InsetTabsComponent, Spinner } from "@swivl-mono/ui";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Member } from "../../../Models/Member/Member.Current";
import { AdditionalCustomizationsTab } from "./Components/AdditionalCustomizationsTab";
import { CustomizedUnmappedTab } from "./Components/CustomizedUnmappedTab";
import { MigrateDetailsTab } from "./Components/MigrateDetailsTab";
import { PoliciesTab } from "./Components/PoliciesTab";
import { MigrationStepHandle } from "./Components/workflow";

type Step = "details" | "policies" | "unmapped" | "custom";
/** Steps with a tracked last run. Details and Policies write from their own Apply; Unmigrated content
 * saves its run automatically once its suggestions load. "New settings" is read-only (Policies' Apply
 * tickets it). */
type ApplyStep = Exclude<Step, "custom">;
const APPLY_STEPS: ApplyStep[] = ["details", "policies", "unmapped"];

const STEP_LABELS: Record<Step, string> = {
  details: "Details",
  policies: "Policies",
  unmapped: "Unmigrated content",
  custom: "New settings"
};

/** A step's last apply, for the overview row and the tab's ✓. null = never applied. */
interface StepStatus {
  at: string;
  summary: React.ReactNode;
}

const ticketLink = (number: number): React.ReactNode => (
  <Link to={`/ticket/${number}`} className="text-orange-500 underline">
    #{number}
  </Link>
);

function stepStatus(step: ApplyStep, status: MigrationStatus | null): StepStatus | null {
  if (!status) return null;
  if (step === "details") return status.detailsLastMigratedAt ? { at: status.detailsLastMigratedAt, summary: null } : null;
  if (step === "policies") {
    const run = status.policyLastRun;
    if (!run) return null;
    return { at: run.at, summary: `${run.appliedFieldCount} field(s) written${run.failedFieldCount > 0 ? `, ${run.failedFieldCount} failed` : ""}` };
  }
  const run = status.unmigratedLastRun;
  if (!run) return null;
  return {
    at: run.at,
    summary:
      run.ticketNumber == null ? (
        `${run.totalCount} archived, nothing actionable`
      ) : (
        <>
          {run.actionableCount}/{run.totalCount} actionable · ticket {ticketLink(run.ticketNumber)}
        </>
      )
  };
}

/**
 * Guided migrator. Opens on an Overview listing each step and when it last ran; opening a step enters
 * the review flow (Details, Policies, Unmigrated content, New settings). Details and Policies each
 * apply on their own, in any order, and can be re-applied; there is no single Accept-all. Unmigrated
 * content has no Apply: it saves its run (S3 archive + CX ticket) as soon as its suggestions load. After one step
 * applies, the others are marked stale and re-fetch their preview when next opened, because a write in
 * one (e.g. Details writing the CortexFeature* keys) can change what another would propose.
 */
export function MigrateToCortexV3View(): React.ReactElement {
  const orgId = Member.orgId;
  const topRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<"overview" | "workflow">("overview");
  const [status, setStatus] = useState<MigrationStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [step, setStep] = useState<Step>("details");
  const [visited, setVisited] = useState<Set<Step>>(() => new Set<Step>(["details"]));
  const [stale, setStale] = useState<Set<ApplyStep>>(() => new Set());
  const detailsRef = useRef<MigrationStepHandle>(null);
  const policiesRef = useRef<MigrationStepHandle>(null);
  const unmappedRef = useRef<MigrationStepHandle>(null);

  const loadStatus = useCallback(async () => {
    if (!orgId) return;
    setStatusLoading(true);
    try {
      setStatus(await API.Cortex.migrationStatus(orgId));
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadStatus().catch(() => {
      /* handled in loadStatus */
    });
  }, [loadStatus]);

  // Snap to the top of the view whenever the step or screen changes.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [step, view]);

  // A stale step re-fetches when it is opened, not the moment another step applies: the Unmigrated
  // step's reload re-runs every suggestion call, which is wasted work if it is never looked at again.
  useEffect(() => {
    if (view !== "workflow" || step === "custom" || !stale.has(step)) return;
    const refs: Record<ApplyStep, RefObject<MigrationStepHandle>> = { details: detailsRef, policies: policiesRef, unmapped: unmappedRef };
    setStale((prev) => {
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
    refs[step].current?.reload().catch(() => {
      /* each tab surfaces its own load error */
    });
  }, [view, step, stale]);

  const handleStepApplied = useCallback(
    (source: ApplyStep) => {
      setStale((prev) => new Set([...prev, ...APPLY_STEPS.filter((s) => s !== source)]));
      loadStatus().catch(() => {
        /* handled in loadStatus */
      });
    },
    [loadStatus]
  );
  const handleDetailsApplied = useCallback(() => handleStepApplied("details"), [handleStepApplied]);
  const handlePoliciesApplied = useCallback(() => handleStepApplied("policies"), [handleStepApplied]);
  const handleUnmappedApplied = useCallback(() => handleStepApplied("unmapped"), [handleStepApplied]);
  // Must stay referentially stable: the Unmigrated tab's reload depends on it, and its load effect
  // re-runs whenever reload changes.
  const handleRunSaved = useCallback(() => {
    loadStatus().catch(() => {
      /* handled in loadStatus */
    });
  }, [loadStatus]);

  const goTo = useCallback((dest: Step) => {
    setStep(dest);
    setVisited((prev) => (prev.has(dest) ? prev : new Set(prev).add(dest)));
  }, []);

  const openWorkflow = useCallback((dest: Step) => {
    setVisited(new Set<Step>([dest]));
    setStale(new Set());
    setStep(dest);
    setView("workflow");
  }, []);

  const handleSelectTab = useCallback((id: string) => goTo(id as Step), [goTo]);
  const handleExitToOverview = useCallback(() => setView("overview"), []);

  if (!orgId) {
    return <div className="body-sm p-6 text-grey-500">No active org — pick one from the property manager first.</div>;
  }

  const items = (Object.keys(STEP_LABELS) as Step[]).map((id, i) => {
    const applied = id !== "custom" && stepStatus(id, status) !== null;
    return { id, label: `${i + 1}. ${STEP_LABELS[id]}${applied ? " ✓" : ""}`, badge: false };
  });

  return (
    <div ref={topRef}>
      {view === "overview" ? (
        <div className="mx-auto max-w-5xl p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="heading-lg">Migrate to Cortex V3</h1>
              <p className="body-sm mt-1 text-grey-500">Review and apply each step on its own, in any order. Every step is safe to re-apply.</p>
            </div>
            <Button variant={ButtonVariant.PRIMARY} onClick={() => openWorkflow("details")}>
              Open migrator
            </Button>
          </div>
          {statusLoading ? (
            <div className="body-sm flex items-center gap-2 p-2 text-grey-400">
              <Spinner /> Loading status…
            </div>
          ) : (
            <ul className="divide-y divide-grey-100 rounded-lg border border-grey-200 bg-white">
              {APPLY_STEPS.map((id) => {
                const s = stepStatus(id, status);
                return (
                  <li key={id} className="flex items-center justify-between gap-4 px-4 py-3">
                    <div>
                      <div className="body-sm font-semibold text-grey-900">
                        {s ? "✓ " : ""}
                        {STEP_LABELS[id]}
                      </div>
                      <div className="body-xs text-grey-500">
                        {s ? (
                          <>
                            {id === "unmapped" ? "Last saved" : "Last applied"} {new Date(s.at).toLocaleString()}
                            {s.summary && <> · {s.summary}</>}
                          </>
                        ) : (
                          "Not applied yet"
                        )}
                      </div>
                    </div>
                    <Button variant={ButtonVariant.GHOST} onClick={() => openWorkflow(id)}>
                      Open
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <>
          <div className="mx-auto max-w-5xl px-6 pt-6">
            <div className="mb-3 flex items-center justify-between">
              <h1 className="heading-lg">Migrate to Cortex V3</h1>
              <Button variant={ButtonVariant.GHOST} onClick={handleExitToOverview}>
                Exit to overview
              </Button>
            </div>
            <InsetTabsComponent items={items} selectedItem={step} itemSelected={handleSelectTab} />
          </div>

          {visited.has("details") && (
            <div className={filterClasses(step !== "details" && "hidden")}>
              <MigrateDetailsTab ref={detailsRef} orgId={orgId} onApplied={handleDetailsApplied} />
            </div>
          )}
          {visited.has("policies") && (
            <div className={filterClasses(step !== "policies" && "hidden")}>
              <PoliciesTab ref={policiesRef} orgId={orgId} onApplied={handlePoliciesApplied} />
            </div>
          )}
          {visited.has("unmapped") && (
            <div className={filterClasses(step !== "unmapped" && "hidden")}>
              <CustomizedUnmappedTab ref={unmappedRef} orgId={orgId} onApplied={handleUnmappedApplied} onRunSaved={handleRunSaved} />
            </div>
          )}
          {visited.has("custom") && (
            <div className={filterClasses(step !== "custom" && "hidden")}>
              <AdditionalCustomizationsTab orgId={orgId} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
